import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClientOnly } from 'vite-react-ssg';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Star } from 'lucide-react';
import { LocationCard } from '@/components/locations/LocationCard';
import { LocationsMap } from '@/components/locations/LocationsMap';
import { getAllLocations, type LocationSummary } from '@/lib/locations';

const LOCATIONS_PER_PAGE = 48;

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

interface LocationBrowserProps {
  onLocationSelect: (location: LocationSummary) => void;
  /** The list from the route loader, so the build renders the cards (ERI-2594). */
  initialLocations?: LocationSummary[];
  selectionMode?: boolean;
  selectedLocationId?: string;
  showMap?: boolean;
  className?: string;
}

export const LocationBrowser: React.FC<LocationBrowserProps> = ({
  onLocationSelect,
  selectionMode = false,
  selectedLocationId,
  showMap = true,
  className,
  initialLocations,
}) => {
  const { data: locations = [], isLoading: loading } = useQuery({
    queryKey: ['locations'],
    queryFn: getAllLocations,
    initialData: initialLocations,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [bortleFilter, setBortleFilter] = useState('all');
  const [bestMonthFilter, setBestMonthFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(LOCATIONS_PER_PAGE);

  // Debounce search input
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setVisibleCount(LOCATIONS_PER_PAGE);
    }, 300);
  }, []);
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const countries = useMemo(() => {
    const set = new Set(locations.map((l) => l.country));
    return Array.from(set).sort();
  }, [locations]);

  const filteredLocations = useMemo(() => {
    let filtered = locations;

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          l.country.toLowerCase().includes(q) ||
          (l.state_province && l.state_province.toLowerCase().includes(q)),
      );
    }

    if (countryFilter !== 'all') {
      filtered = filtered.filter((l) => l.country === countryFilter);
    }

    if (bortleFilter !== 'all') {
      const maxBortle = Number(bortleFilter);
      filtered = filtered.filter((l) => l.bortle_class !== null && l.bortle_class <= maxBortle);
    }

    if (bestMonthFilter !== 'all') {
      const month = Number(bestMonthFilter);
      filtered = filtered.filter((l) => l.best_months?.includes(month));
    }

    return filtered;
  }, [locations, debouncedSearch, countryFilter, bortleFilter, bestMonthFilter]);

  const visibleLocations = useMemo(
    () => filteredLocations.slice(0, visibleCount),
    [filteredLocations, visibleCount],
  );
  const hasMore = visibleCount < filteredLocations.length;

  const clearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setCountryFilter('all');
    setBortleFilter('all');
    setBestMonthFilter('all');
    setVisibleCount(LOCATIONS_PER_PAGE);
  };

  const hasActiveFilters = searchQuery || countryFilter !== 'all' || bortleFilter !== 'all' || bestMonthFilter !== 'all';

  return (
    <div className={className}>
      {/* Search */}
      {selectionMode && (
        <div className="mb-6">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by name, country, or state..."
              className="pl-10 h-10 rounded-[4px]"
            />
          </div>
        </div>
      )}

      {/* Map */}
      {showMap && !loading && (
        <div className="mb-8">
          {/* Leaflet needs a browser; the build renders the cards without the map (ERI-2594). */}
          <ClientOnly fallback={<div className="w-full h-[400px] bg-[#0f1923] rounded-[4px]" />}>
            {() => (
          <LocationsMap
            locations={filteredLocations}
            onMarkerClick={(slug) => {
              const loc = locations.find((l) => l.slug === slug);
              if (loc) onLocationSelect(loc);
            }}
          />
            )}
          </ClientOnly>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        {!selectionMode && (
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search locations..."
              className="pl-10 h-9 rounded-[4px] w-[200px]"
            />
          </div>
        )}

        <Select value={countryFilter} onValueChange={(v) => { setCountryFilter(v); setVisibleCount(LOCATIONS_PER_PAGE); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Countries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {countries.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={bortleFilter} onValueChange={(v) => { setBortleFilter(v); setVisibleCount(LOCATIONS_PER_PAGE); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Bortle Class" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Bortle</SelectItem>
            <SelectItem value="2">Bortle 1-2</SelectItem>
            <SelectItem value="3">Bortle 1-3</SelectItem>
            <SelectItem value="4">Bortle 1-4</SelectItem>
            <SelectItem value="5">Bortle 1-5</SelectItem>
          </SelectContent>
        </Select>

        <Select value={bestMonthFilter} onValueChange={(v) => { setBestMonthFilter(v); setVisibleCount(LOCATIONS_PER_PAGE); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Best Month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Month</SelectItem>
            {MONTH_NAMES.slice(1).map((name, i) => (
              <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-4 ml-auto">
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
              Clear filters
            </Button>
          )}
          <span className="text-sm text-muted-foreground">
            {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-40 bg-muted rounded-t-[4px]" />
              <div className="h-32 bg-muted/50 rounded-b-[4px]" />
            </div>
          ))}
        </div>
      ) : filteredLocations.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleLocations.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                onSelect={selectionMode ? onLocationSelect : undefined}
                isSelected={selectionMode && selectedLocationId === location.id}
              />
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-8">
              <Button
                variant="outline"
                onClick={() => setVisibleCount((c) => c + LOCATIONS_PER_PAGE)}
                className="rounded-[4px]"
              >
                Show More ({filteredLocations.length - visibleCount} remaining)
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <Star className="w-12 h-12 text-muted-foreground/20 mx-auto mb-6" />
          <h2 className="font-serif text-2xl font-bold text-foreground mb-3">
            No locations match your filters
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
            Try adjusting your search or filters to find dark sky locations.
          </p>
          <Button variant="outline" className="rounded-[4px]" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      )}
    </div>
  );
};
